import { alpha, Box, IconButton, MenuItem, Stack, styled } from '@mui/material';
import { primitives, tokens } from '@/styles/tokens';

/** Class hook so a row can reveal its hover-only controls. */
export const REVEAL_CLASS = 'doc-panel-reveal';

/** Class hook on a legend row's trailing action slot, so its body can hide it. */
export const ACTIONS_CLASS = 'doc-panel-actions';

export const PanelRoot = styled(Stack)({
  width: '100%',
});

export const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'grow',
})<{ grow?: boolean }>(({ grow }) => ({
  display: 'flex',
  flexDirection: 'column',
  // Figma 36557-195099: the label butts against its content — the 4px of air
  // comes from the label's own padding, not a gap.
  gap: 0,
  ...(grow && { flex: 1, minHeight: 120 }),
}));

export const SectionDivider = styled(Box)({
  height: 1,
  backgroundColor: tokens.grey[90],
});

export const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  // Figma `label`: 14px text with 4px above and below.
  minHeight: 24,
  padding: '4px 0',
});

export const SectionLabelGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const SectionLabel = styled(Box)({
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
  color: tokens.text.secondary,
});

export const SectionDescription = styled(Box)({
  fontSize: 12,
  fontWeight: 500,
  color: tokens.grey[50],
  lineHeight: 1.36,
  marginBottom: 8,
});

export const LegendList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

/**
 * Holds the caller's loading node. Same vertical rhythm as the empty state, so the
 * block keeps its height whichever of the two it is showing.
 */
export const LoadingLegend = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 0',
});

export const EmptyLegend = styled(Box)({
  fontSize: 14,
  fontWeight: 500,
  color: alpha(tokens.grey[50], 0.5),
  padding: '16px 0',
  textAlign: 'center',
});

/**
 * One legend entry (Figma `tickmark-input`, 36557-198995): a 48px-min card. At
 * rest it is transparent and fills grey #F8F8FA on hover; while FOCUSED it fills
 * brand/10 #EFF9FA (the teal active band) and keeps that teal on hover. Rows stack
 * flush — the card's own 4/8 padding is the only separation.
 */
export const LegendRowRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted?: boolean }>(({ highlighted }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  minHeight: 48,
  padding: '4px 8px',
  borderRadius: 8,
  boxSizing: 'border-box',
  backgroundColor: highlighted ? primitives.brand[10] : 'transparent',
  transition: 'background-color 0.2s ease',
  '&:hover': { backgroundColor: highlighted ? primitives.brand[10] : tokens.grey[120] },
  // Reveal the row's hover-only controls (kebab) on hover OR while focused.
  [`&:hover .${REVEAL_CLASS}`]: {
    opacity: 1,
  },
  ...(highlighted && {
    [`& .${REVEAL_CLASS}`]: { opacity: 1 },
  }),
}));

export const LegendLeading = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  // Figma pins the glyph in a 24x40 slot (8px above/below), so it sits on the
  // first line of a wrapped label instead of centring on the whole row.
  height: 40,
});

export const LegendBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hideActionsWhileEditing',
})<{ hideActionsWhileEditing?: boolean }>(({ hideActionsWhileEditing }) => ({
  flex: 1,
  minWidth: 0,
  // A selector rather than focus/blur state: it follows the real focus, even when the focused
  // toolbar button is removed and no blur fires. `:has(:focus)` means `:focus-within` here
  // (the body itself never takes focus), and jsdom can evaluate it. `visibility` keeps the
  // slot's width, so the body does not reflow.
  ...(hideActionsWhileEditing && {
    [`&:has(:focus) + .${ACTIONS_CLASS}`]: { visibility: 'hidden' },
  }),
}));

export const LegendActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  // Same 40px slot as the glyph, so counter + kebab stay on the label's first line.
  height: 40,
});

/**
 * The mark-glyph slot — a 24×24 box pinning the glyph. Shared so the editor and
 * data-analysis legends render the mark chrome identically; interactive rows
 * (click-to-change-type) get the button + hover fill, static ones the plain span.
 */
const legendGlyphBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  border: 'none',
  borderRadius: 6,
  background: 'none',
  flexShrink: 0,
} as const;

export const LegendGlyphButton = styled('button')(({ theme }) => ({
  ...legendGlyphBase,
  cursor: 'pointer',
  '&:disabled': { cursor: 'default' },
  '&:hover:not(:disabled)': { backgroundColor: theme.palette.action.hover },
}));

export const LegendGlyphStatic = styled('span')(legendGlyphBase);

/**
 * Usage-counter pill (Figma `counter`) — opens the "N places" popover. Resting: a
 * filled grey pill. FOCUSED (`active`): the teal variant from Figma 36557-198995 —
 * brand/50 border, brand/10 fill, brand/125 ink — so it reads as part of the active
 * legend band.
 */
const legendCounterBase = (active: boolean | undefined) =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    padding: '4px 8px',
    borderRadius: 80,
    border: `1px solid ${active ? primitives.brand[85] : tokens.grey[90]}`,
    backgroundColor: active ? primitives.brand[10] : tokens.grey[120],
    color: active ? primitives.brand[125] : tokens.text.secondary,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
  }) as const;

export const LegendCounter = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  ...legendCounterBase(active),
  cursor: 'pointer',
  '&:hover': active
    ? { borderColor: primitives.brand[125] }
    : { borderColor: tokens.grey[50], color: theme.palette.text.primary },
}));

/** The same pill as a plain label, for a surface whose count opens nothing. */
export const LegendCounterStatic = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => legendCounterBase(active));

/** Figma 41164:71330: a 0.5px rule between the tickmark block and the note block. */
export const LegendBlocksDivider = styled('div')(({ theme }) => ({
  height: 0,
  margin: '4px 8px',
  borderTop: `0.5px solid ${theme.palette.grey[100]}`,
}));

export const RevealBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'forceVisible',
})<{ forceVisible?: boolean }>(({ forceVisible, theme }) => ({
  display: 'inline-flex',
  opacity: forceVisible ? 1 : 0,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
}));

export const MenuButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  width: 24,
  height: 24,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.divider, 0.5),
  },
  '& svg': {
    width: 16,
    height: 16,
  },
}));

/** `refused`: drawn as MUI draws a disabled item, but still hoverable, for its tooltip. */
export const DeleteMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'refused',
})<{ refused?: boolean }>(({ theme, refused }) => ({
  color: theme.palette.error.main,
  fontSize: 13,
  fontWeight: 500,
  gap: 8,
  '& svg': {
    width: 14,
    height: 14,
  },
  ...(refused && {
    opacity: theme.palette.action.disabledOpacity,
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  }),
}));
